import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  BarChart3,
  Calendar,
  IndianRupee
} from 'lucide-react';
import { 
  formatCurrency,
  MonthlyAnalysis,
  DailyEODBalance,
  IncomeExpenseData
} from '../utils/bankStatementUtils';

// Simple Bar Chart Component (CSS-based)
const SimpleBarChart: React.FC<{
  data: { label: string; value: number; color?: string }[];
  title: string;
  maxValue?: number;
}> = ({ data, title, maxValue }) => {
  const max = maxValue || Math.max(...data.map(d => d.value));
  
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          {title}
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {data.map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-20 text-sm text-gray-600 truncate">{item.label}</div>
              <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                <div 
                  className={`h-4 rounded-full ${item.color || 'bg-blue-500'} transition-all duration-300`}
                  style={{ width: `${(item.value / max) * 100}%` }}
                ></div>
              </div>
              <div className="w-24 text-sm font-semibold text-gray-900 text-right">
                {formatCurrency(item.value)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Monthly Trends Chart Component
export const MonthlyTrendsChart: React.FC<{ data: MonthlyAnalysis[] }> = ({ data }) => {
  if (!data.length) return null;

  const chartData = data.slice(0, 6).map(month => {
    const credits = parseFloat(month.TotalCredits.replace(/[₹,]/g, '') || '0');
    const debits = parseFloat(month.TotalDebits.replace(/[₹,]/g, '') || '0');
    
    return {
      month: month.Month,
      credits,
      debits,
      netFlow: credits - debits
    };
  });

  const maxValue = Math.max(...chartData.flatMap(d => [d.credits, d.debits]));

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Calendar className="w-5 h-5 mr-2" />
          Monthly Cash Flow Trends
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-6">
          {chartData.map((month, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{month.month}</span>
                <span className={`text-sm font-semibold ${
                  month.netFlow >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  Net: {formatCurrency(month.netFlow)}
                </span>
              </div>
              
              {/* Credits Bar */}
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div 
                    className="h-3 bg-green-500 rounded-full transition-all duration-300"
                    style={{ width: `${(month.credits / maxValue) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-green-600 w-20 text-right">
                  {formatCurrency(month.credits)}
                </span>
              </div>
              
              {/* Debits Bar */}
              <div className="flex items-center space-x-2">
                <TrendingDown className="w-4 h-4 text-red-500" />
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div 
                    className="h-3 bg-red-500 rounded-full transition-all duration-300"
                    style={{ width: `${(month.debits / maxValue) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-red-600 w-20 text-right">
                  {formatCurrency(month.debits)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Balance Timeline Component
export const BalanceTimelineChart: React.FC<{ data: DailyEODBalance[] }> = ({ data }) => {
  if (!data.length) return null;

  // Sample every 10th day to avoid overcrowding
  const sampledData = data.filter((_, index) => index % 10 === 0).slice(0, 12);
  const maxBalance = Math.max(...sampledData.map(d => d['EOD Balance']));
  const minBalance = Math.min(...sampledData.map(d => d['EOD Balance']));
  const range = maxBalance - minBalance;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <IndianRupee className="w-5 h-5 mr-2" />
          Balance Timeline
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-3">
          {sampledData.map((balance, index) => {
            const date = new Date(balance.Date).toLocaleDateString('en-IN', { 
              month: 'short', 
              day: 'numeric' 
            });
            const balanceValue = balance['EOD Balance'];
            const heightPercentage = range > 0 ? ((balanceValue - minBalance) / range) * 100 : 50;
            
            return (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-16 text-xs text-gray-600">{date}</div>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div 
                    className={`h-6 rounded-full transition-all duration-300 ${
                      balanceValue >= 0 ? 'bg-blue-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.max(heightPercentage, 5)}%` }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xs font-medium ${
                      heightPercentage >= 50 
                        ? 'text-white drop-shadow-sm' 
                        : balanceValue >= 0 
                          ? 'text-blue-900' 
                          : 'text-red-900'
                    }`}>
                      {formatCurrency(balanceValue)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Min: {formatCurrency(minBalance)}</span>
            <span>Max: {formatCurrency(maxBalance)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Income vs Expense Comparison Chart
export const IncomeExpenseChart: React.FC<{ data: IncomeExpenseData[] }> = ({ data }) => {
  if (!data.length) return null;

  const maxValue = Math.max(...data.flatMap(d => [d.MonthlyIncome, d.MonthlyExpense]));

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Income vs Expense Analysis
        </h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {data.slice(0, 6).map((month, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{month.Month}</span>
                <span className={`text-sm font-semibold ${
                  month.CurrentBalance >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  Balance: {formatCurrency(month.CurrentBalance)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Income */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-green-600">Income</span>
                    <span className="text-xs font-medium text-green-600">
                      {formatCurrency(month.MonthlyIncome)}
                    </span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 bg-green-500 rounded-full transition-all duration-300"
                      style={{ width: `${(month.MonthlyIncome / maxValue) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Expense */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-red-600">Expense</span>
                    <span className="text-xs font-medium text-red-600">
                      {formatCurrency(month.MonthlyExpense)}
                    </span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 bg-red-500 rounded-full transition-all duration-300"
                      style={{ width: `${(month.MonthlyExpense / maxValue) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SimpleBarChart;
